import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Checkbox,
  FormControlLabel,
  CircularProgress,
  Alert,
  Chip,
  Divider,
} from "@mui/material";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import { RxDtoProcessingStatusEnum } from "../../../../api-client";
import { useUpdateRxProcessingStatusWithCleanup } from "../../../../stores/Rx/rx.mutations";
import notificationStore from "../../../../stores/Notification/notificationStore";

/** Shape returned by the backend in `completableRxs` after a swap */
export interface CompletableRx {
  rxId: string;
  rxNumber: string;
  allAttended: boolean;
  hasUnscheduled: boolean;
  unscheduledCount?: number;
}

interface PostSwapRxCompletionDialogProps {
  open: boolean;
  onClose: () => void;
  completableRxs: CompletableRx[];
  patientId: number;
}

/**
 * Dialog shown after a series-swap when one or more Rx are now fully attended.
 * Allows the user to set them to "abgeschlossen" and optionally remove
 * remaining unscheduled appointments.
 */
const PostSwapRxCompletionDialog: React.FC<PostSwapRxCompletionDialogProps> = ({
  open,
  onClose,
  completableRxs,
  patientId,
}) => {
  // Pre-select all completable Rx
  const [selectedRxIds, setSelectedRxIds] = useState<Set<string>>(
    () => new Set(completableRxs.map((rx) => rx.rxId))
  );

  // Sync selectedRxIds when completableRxs changes (e.g. dialog re-opens with new data)
  useEffect(() => {
    setSelectedRxIds(new Set(completableRxs.map((rx) => rx.rxId)));
  }, [completableRxs]);

  const [isProcessing, setIsProcessing] = useState(false);
  const [processedCount, setProcessedCount] = useState(0);

  const updateStatusMutation = useUpdateRxProcessingStatusWithCleanup();

  const toggleRx = (rxId: string) => {
    setSelectedRxIds((prev) => {
      const next = new Set(prev);
      if (next.has(rxId)) {
        next.delete(rxId);
      } else {
        next.add(rxId);
      }
      return next;
    });
  };

  const handleConfirm = async () => {
    setIsProcessing(true);
    setProcessedCount(0);
    let successCount = 0;
    let errorCount = 0;

    for (const rx of completableRxs) {
      if (!selectedRxIds.has(rx.rxId)) continue;

      try {
        await updateStatusMutation.mutateAsync({
          rxId: rx.rxId,
          status: RxDtoProcessingStatusEnum.Abgeschlossen,
          patientId,
          removeUnscheduled: rx.hasUnscheduled,
        });
        successCount++;
      } catch (error: any) {
        errorCount++;
        console.error(`Failed to complete Rx ${rx.rxNumber}:`, error);
      }
      setProcessedCount((prev) => prev + 1);
    }

    setIsProcessing(false);

    if (successCount > 0 && errorCount === 0) {
      notificationStore.showMessage(
        `${successCount} Rezept${successCount > 1 ? "e" : ""} erfolgreich abgeschlossen`,
        "success"
      );
    } else if (errorCount > 0) {
      notificationStore.showMessage(
        `${successCount} abgeschlossen, ${errorCount} fehlgeschlagen`,
        errorCount === completableRxs.length ? "error" : "warning"
      );
    }

    onClose();
  };

  const selectedCount = selectedRxIds.size;
  const totalUnscheduled = completableRxs
    .filter((rx) => selectedRxIds.has(rx.rxId) && rx.hasUnscheduled)
    .reduce((sum, rx) => sum + (rx.unscheduledCount || 0), 0);

  return (
    <Dialog open={open} onClose={isProcessing ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle
        sx={{ display: "flex", alignItems: "center", gap: 1 }}
      >
        <CheckCircleOutlineIcon color="success" />
        Rezepte abschließen
      </DialogTitle>

      <DialogContent>
        <Typography variant="body1" sx={{ mb: 2 }}>
          Nach dem Neuordnen sind folgende Rezepte vollständig behandelt. Möchtest
          du den Status auf <strong>„abgeschlossen"</strong> setzen?
        </Typography>

        <Box sx={{ display: "flex", flexDirection: "column", gap: 1, mb: 2 }}>
          {completableRxs.map((rx) => (
            <Box
              key={rx.rxId}
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                p: 1.5,
                borderRadius: 1,
                border: "1px solid",
                borderColor: selectedRxIds.has(rx.rxId) ? "primary.main" : "divider",
                bgcolor: selectedRxIds.has(rx.rxId) ? "action.selected" : "transparent",
                transition: "all 0.2s",
                cursor: isProcessing ? "default" : "pointer",
                "&:hover": !isProcessing
                  ? { bgcolor: "action.hover" }
                  : undefined,
              }}
              onClick={() => !isProcessing && toggleRx(rx.rxId)}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={selectedRxIds.has(rx.rxId)}
                      disabled={isProcessing}
                      onClick={(e) => e.stopPropagation()}
                      onChange={() => toggleRx(rx.rxId)}
                    />
                  }
                  label={
                    <Typography variant="body1" fontWeight="bold">
                      Rezept {rx.rxNumber || rx.rxId.slice(0, 8)}
                    </Typography>
                  }
                />
              </Box>

              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                {rx.hasUnscheduled && rx.unscheduledCount ? (
                  <Chip
                    icon={<WarningAmberIcon />}
                    label={`${rx.unscheduledCount} ungeplante Termine`}
                    color="warning"
                    size="small"
                    variant="outlined"
                  />
                ) : (
                  <Chip
                    icon={<CheckCircleOutlineIcon />}
                    label="Bereit"
                    color="success"
                    size="small"
                    variant="outlined"
                  />
                )}
              </Box>
            </Box>
          ))}
        </Box>

        {totalUnscheduled > 0 && (
          <>
            <Divider sx={{ my: 1.5 }} />
            <Alert severity="info" sx={{ mt: 1 }}>
              <Typography variant="body2">
                <strong>{totalUnscheduled}</strong> ungeplante Termine werden beim
                Abschließen automatisch entfernt.
              </Typography>
            </Alert>
          </>
        )}

        {isProcessing && (
          <Box sx={{ display: "flex", alignItems: "center", gap: 2, mt: 2 }}>
            <CircularProgress size={20} />
            <Typography variant="body2" color="text.secondary">
              Verarbeite {processedCount} / {selectedCount} Rezepte…
            </Typography>
          </Box>
        )}
      </DialogContent>

      <DialogActions>
        <Button
          variant="text"
          onClick={onClose}
          disabled={isProcessing}
        >
          Überspringen
        </Button>
        <Button
          variant="contained"
          onClick={handleConfirm}
          disabled={isProcessing || selectedCount === 0}
          startIcon={isProcessing ? <CircularProgress size={16} /> : undefined}
        >
          {selectedCount > 0
            ? `${selectedCount} Rezept${selectedCount > 1 ? "e" : ""} abschließen`
            : "Keine ausgewählt"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PostSwapRxCompletionDialog;
